/**
 * Market -> IANA time zone for campaign windows.
 *
 * A leaf module on purpose. `campaigns.ts` imports `@/sanity/fetch`, and the
 * campaign-price gate in `productDisplay.ts` is reached from client components
 * (`ProductCard`, `ProductsGridSectionClient`) — so importing the map from
 * there would pull the Sanity server client into the client bundle. Keeping it
 * dependency-free lets both sides share one source of truth instead of the
 * table being copied.
 */
const CAMPAIGN_TIME_ZONE_BY_MARKET: Record<string, string> = {
  AT: 'Europe/Vienna',
  AU: 'Australia/Sydney',
  CA: 'America/Toronto',
  CZ: 'Europe/Prague',
  DE: 'Europe/Berlin',
  DK: 'Europe/Copenhagen',
  ES: 'Europe/Madrid',
  FI: 'Europe/Helsinki',
  FR: 'Europe/Paris',
  GB: 'Europe/London',
  IT: 'Europe/Rome',
  NL: 'Europe/Amsterdam',
  NO: 'Europe/Oslo',
  PL: 'Europe/Warsaw',
  SE: 'Europe/Stockholm',
  US: 'America/New_York',
}

export const campaignTimeZoneForMarket = (market?: string | null) =>
  CAMPAIGN_TIME_ZONE_BY_MARKET[
    String(market || '')
      .trim()
      .toUpperCase()
  ] || 'UTC'

/**
 * The calendar date in a market's local time, as `YYYY-MM-DD`.
 *
 * Feed-PIM sends `campaignFrom`/`campaignTo` as bare dates with no time and no
 * offset, so the only correct comparison is calendar-date to calendar-date in
 * the market's own zone. Converting the bounds to instants instead would make
 * a campaign open or close at the wrong hour in every market but one.
 *
 * `en-CA` is used purely because it formats as `YYYY-MM-DD`, which then
 * compares correctly with a plain string comparison.
 */
export const marketLocalDate = (now: Date, market?: string | null): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: campaignTimeZoneForMarket(market),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)

/** A bare `YYYY-MM-DD`. Anything else is treated as no bound at all. */
const asCalendarDate = (value?: string | null): string | undefined => {
  const trimmed = String(value ?? '').trim()
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined
}

export type CampaignWindow = {
  campaignFrom?: string | null
  campaignTo?: string | null
}

/**
 * Is a variant's campaign price live in this market right now?
 *
 * Both bounds are **inclusive**: a campaign running 31 Aug - 27 Sep is on sale
 * for the whole of both days in that market's local time.
 *
 * Reported by Daniel 2026-08-28: campaign prices were visible before the
 * campaign opened, because nothing checked the window — Feed-PIM did not send
 * one at variant level until now.
 */
export const isCampaignWindowActive = (
  window: CampaignWindow | null | undefined,
  market?: string | null,
  now: Date = new Date(),
): boolean => {
  const from = asCalendarDate(window?.campaignFrom)
  const to = asCalendarDate(window?.campaignTo)

  // No window at all -> not on campaign. Fail closed: showing a campaign price
  // early is the bug being fixed, and stale `campaignAmount` values without
  // dates are exactly the data that caused it. See CAMPAIGN_PRICE_WITHOUT_
  // WINDOW in productDisplay.ts for the single place this is decided.
  if (!from && !to) return false

  const today = marketLocalDate(now, market)
  if (from && today < from) return false
  if (to && today > to) return false
  return true
}
